import React, { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { 
  Home, 
  Edit3, 
  Play, 
  Gamepad2, 
  Settings, 
  Globe, 
  Bell, 
  User,
  Plus,
  Sparkles,
  X,
  Save,
  Image,
  Brain,
  LogIn,
  LogOut,
  UserCircle,
  HelpCircle,
  MessageCircle
} from 'lucide-react'
import { useStoryStore } from '../stores/storyStore'
import { useAuth } from '../stores/authStore'
import settingsService from '../services/settingsService'
import AuthModal from './AuthModal'
import UserProfile from './UserProfile'
import UserSettings from './UserSettings'
import { UserCenter } from './UserCenter'
import HelpModal from './HelpModal'
import toast from 'react-hot-toast'
import { useTheme } from '../contexts/ThemeContext'

export const Layout = ({ children }) => {
  const [language, setLanguage] = useState('中文')
  const [showSettings, setShowSettings] = useState(false)
  const [showAuthModal, setShowAuthModal] = useState(false)
  const [showUserProfile, setShowUserProfile] = useState(false)
  const [showUserSettings, setShowUserSettings] = useState(false)
  const [showUserCenter, setShowUserCenter] = useState(false)
  const [showHelp, setShowHelp] = useState(false)
  const [settings, setSettings] = useState(() => {
    const savedSettings = settingsService.getSettings()
    console.log('初始化时加载的设置:', savedSettings)
    return savedSettings
  })
  const [connectionStatus, setConnectionStatus] = useState({
    language: { connected: false, testing: false },
    image: { connected: false, testing: false }
  })
  const location = useLocation()
  const navigate = useNavigate()
  const { stories } = useStoryStore()
  const { user, isAuthenticated, logout } = useAuth()
  const { theme } = useTheme()

  const isActive = (path) => {
    if (path === '/explore') {
      // 探索页路径包括 /explore, /workshop, /world-simulator（但不包括详情页）
      return location.pathname === '/explore' || 
             location.pathname === '/workshop' || 
             (location.pathname === '/world-simulator' && !location.pathname.includes('/detail') && !location.pathname.includes('/experience') && !location.pathname.includes('/create'))
    }
    if (path === '/') {
      return location.pathname === '/'
    }
    return location.pathname.startsWith(path)
  }

  const isEditorPage = location.pathname.startsWith('/editor')

  // 认证相关处理函数
  const handleAuthSuccess = (user) => {
    setShowAuthModal(false)
    toast.success(`欢迎回来，${user.username}！`)
    // 认证状态已经通过useAuth hook自动更新，这里不需要额外操作
  }

  const handleLogout = async () => {
    try {
      await logout()
      toast.success('已安全登出')
      setShowUserProfile(false)
    } catch (error) {
      toast.error('登出失败')
    }
  }

  // 设置面板处理函数
  const handleSettingsChange = (key, value) => {
    setSettings(prev => {
      const newSettings = { ...prev, [key]: value }
      
      // 当语言模型改变时，自动更新对应的API URL
      if (key === 'languageModel') {
        switch (value) {
          case 'gemini-2.5-pro':
            newSettings.languageApiUrl = 'https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-pro:generateContent'
            break
          case 'gemini-2.0-flash-exp':
            newSettings.languageApiUrl = 'https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash-exp:generateContent'
            break
          case 'deepseek-chat':
            newSettings.languageApiUrl = 'https://api.deepseek.com/v1/chat/completions'
            break
          default:
            break
        }
      }
      
      // 当图像模型改变时，自动更新对应的API URL
      if (key === 'imageModel') {
        switch (value) {
          case 'doubao-seedream-4-0':
            newSettings.imageApiUrl = '/api/doubao'
            break
          case 'gemini-2.5-flash-image-preview':
            newSettings.imageApiUrl = 'https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash-image-preview:generateContent'
            break
          case 'gemini-2.0-flash-exp':
            newSettings.imageApiUrl = 'https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash-exp:generateContent'
            break
          case 'comfyui-local':
            newSettings.imageApiUrl = 'http://localhost:8188/prompt'
            break
          default:
            break
        }
      }
      
      // 对于API Key相关字段，立即保存到localStorage
      if (['languageApiKey', 'imageApiKey', 'languageApiUrl', 'imageApiUrl', 'languageModel', 'imageModel'].includes(key)) {
        setTimeout(() => {
          settingsService.updateSetting(key, value)
          console.log(`🔧 实时保存设置: ${key} = ${key.includes('Key') ? '***已保存***' : value}`)
        }, 100)
      }
      
      return newSettings
    })
  }

  const handleSaveSettings = () => {
    // 使用设置服务保存
    const success = settingsService.saveSettings(settings)
    if (success) {
      toast.success('设置已保存')
      setShowSettings(false)
    } else {
      toast.error('保存设置失败')
    }
  }

  const handleTestLanguageApiConnection = async () => {
    if (!settings.languageApiKey || !settings.languageApiUrl) {
      toast.error('请先配置语言模型API Key和API URL')
      return
    }

    setConnectionStatus(prev => ({
      ...prev,
      language: { ...prev.language, testing: true }
    }))

    toast.loading('正在测试语言模型连接...')
    
    try {
      // 先保存当前设置到 settingsService
      settingsService.updateSetting('languageModel', settings.languageModel)
      settingsService.updateSetting('languageApiKey', settings.languageApiKey)
      settingsService.updateSetting('languageApiUrl', settings.languageApiUrl)
      
      console.log('测试语言模型连接，配置:', {
        model: settings.languageModel,
        apiKey: settings.languageApiKey ? '已设置' : '未设置',
        apiUrl: settings.languageApiUrl
      })
      
      const result = await settingsService.testLanguageApiConnection()
      
      console.log('语言模型连接测试结果:', result)
      
      setConnectionStatus(prev => {
        const newStatus = {
          ...prev,
          language: { connected: result.success, testing: false }
        }
        console.log('更新连接状态:', newStatus)
        return newStatus
      })

      if (result.success) {
        toast.success('语言模型连接测试成功！')
      } else {
        toast.error('语言模型连接测试失败：' + result.error)
      }
    } catch (error) {
      setConnectionStatus(prev => ({
        ...prev,
        language: { connected: false, testing: false }
      }))
      toast.error('语言模型连接测试失败：' + error.message)
    }
  }

  const handleTestApiConnection = async () => {
    if (!settings.imageApiKey || !settings.imageApiUrl) {
      toast.error('请先配置文生图API Key和API URL')
      return
    }

    setConnectionStatus(prev => ({
      ...prev,
      image: { ...prev.image, testing: true }
    }))

    toast.loading('正在测试文生图API连接...')
    
    try {
      // 先保存当前设置到 settingsService
      settingsService.updateSetting('imageModel', settings.imageModel)
      settingsService.updateSetting('imageApiKey', settings.imageApiKey)
      settingsService.updateSetting('imageApiUrl', settings.imageApiUrl)
      
      console.log('测试文生图API连接，配置:', {
        model: settings.imageModel,
        apiKey: settings.imageApiKey ? '已设置' : '未设置',
        apiUrl: settings.imageApiUrl
      })
      
      const result = await settingsService.testImageApiConnection()
      
      console.log('文生图API连接测试结果:', result)
      
      setConnectionStatus(prev => {
        const newStatus = {
          ...prev,
          image: { connected: result.success, testing: false }
        }
        console.log('更新连接状态:', newStatus)
        return newStatus
      })

      if (result.success) {
        toast.success('文生图API连接测试成功！')
      } else {
        toast.error('文生图API连接测试失败：' + result.error)
      }
    } catch (error) {
      setConnectionStatus(prev => ({
        ...prev,
        image: { connected: false, testing: false }
      }))
      toast.error('文生图API连接测试失败：' + error.message)
    }
  }


  return (
    <div className="min-h-screen">
      {/* 顶部导航栏 */}
      <div className="sticky top-0 z-[100] flex h-16 shrink-0 items-center gap-x-4 border-b border-dark-700 bg-dark-800/95 backdrop-blur-sm px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8">
        {/* Logo和品牌名 - 可点击跳转到首页 */}
        <div className="flex items-center">
          <button
            onClick={() => navigate('/')}
            className="flex items-center space-x-3 hover:opacity-80 transition-opacity"
          >
            <div className="w-8 h-8 bg-gradient-to-br from-primary to-secondary rounded-lg flex items-center justify-center">
              <Sparkles size={20} className="text-white" />
            </div>
            <div className="flex items-center justify-center">
              <h1 className="text-3xl font-black text-white tracking-wide brand-glow">AltVerse</h1>
            </div>
          </button>
        </div>

        {/* 中间空白区域 */}
        <div className="flex flex-1"></div>

        {/* 右侧工具栏 */}
        <div className="flex items-center gap-x-4 lg:gap-x-6">
          {/* 探索 */}
          <Link
            to="/explore"
            className={`flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
              isActive('/explore')
                ? 'bg-primary/20 text-primary border border-primary/30'
                : 'text-dark-300 hover:text-white hover:bg-dark-700'
            }`}
          >
            <Sparkles size={18} className="mr-2" />
            探索
          </Link>
          
          {/* 我的世界 */}
          <Link
            to="/my-world"
            className={`flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
              isActive('/my-world') || isActive('/my-games')
                ? 'bg-primary/20 text-primary border border-primary/30'
                : 'text-dark-300 hover:text-white hover:bg-dark-700'
            }`}
          >
            <User size={18} className="mr-2" />
            我的世界
          </Link>


          {/* 帮助 */}
          <button 
            onClick={() => setShowHelp(true)}
            className="text-dark-400 hover:text-white p-2 rounded-md hover:bg-dark-700 transition-colors"
          >
            <HelpCircle size={20} />
          </button>

          {/* 系统设置 */}
          <button 
            onClick={() => setShowSettings(true)}
            className="text-dark-400 hover:text-white p-2 rounded-md hover:bg-dark-700 transition-colors"
            title="系统设置 (API配置)"
          >
            <Settings size={20} />
          </button>


          {/* 语言选择 */}
          <div className="relative">
            <button
              onClick={() => setLanguage(language === '中文' ? 'English' : '中文')}
              className="flex items-center gap-x-2 text-dark-400 hover:text-white p-2 rounded-md hover:bg-dark-700 transition-colors"
            >
              <Globe size={20} />
              <span className="text-sm">{language}</span>
            </button>
          </div>

          {/* 用户认证区域 */}
          {isAuthenticated ? (
            <div className="flex items-center gap-3">
              {/* 用户信息 */}
              <div className="text-right">
                <p className="text-white text-sm font-medium">
                  {user?.username}
                </p>
                <p className="text-dark-400 text-xs">
                  {user?.role === 'user' ? '用户' : user?.role === 'admin' ? '管理员' : '访客'}
                </p>
              </div>
              
              {/* 用户头像 */}
              <button
                onClick={() => setShowUserCenter(true)}
                className="flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 text-white text-sm font-medium hover:from-cyan-500 hover:to-blue-600 transition-all duration-300 shadow-lg shadow-cyan-500/25"
              >
                {user?.username?.charAt(0) || 'U'}
              </button>
            </div>
          ) : (
            <button
              onClick={() => setShowAuthModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white text-sm font-medium rounded-xl transition-all duration-300 shadow-lg shadow-cyan-500/25 hover:shadow-xl hover:shadow-cyan-500/40"
            >
              <LogIn size={16} />
              登录
            </button>
          )}
        </div>
      </div>

      {/* 主内容区域 */}
      <div className="w-full">
        {/* 页面内容 */}
        {isEditorPage ? (
          <main className="h-[calc(100vh-64px)]">
            <div className="h-full w-full">
              {children}
            </div>
          </main>
        ) : location.pathname === '/' || location.pathname === '/explore' ? (
          <main className="w-full">
            {children}
          </main>
        ) : (
          <main className="py-6">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              {children}
            </div>
          </main>
        )}
      </div>

      {/* 设置面板模态框 */}
      {showSettings && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-dark-800 rounded-lg p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-white flex items-center">
                <Settings size={24} className="mr-3 text-primary" />
                系统设置
              </h3>
              <button
                onClick={() => setShowSettings(false)}
                className="text-dark-400 hover:text-white transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            <div className="space-y-6">
              {/* 配置状态指示器 */}
              <div className="bg-dark-700 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-lg font-medium text-white">配置状态</h4>
                  <button
                    onClick={() => {
                      setSettings(prev => ({
                        ...prev,
                        languageModel: 'gemini-2.5-pro',
                        languageApiUrl: 'https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-pro:generateContent',
                        imageModel: 'gemini-2.5-flash-image-preview',
                        imageApiUrl: 'https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash-image-preview:generateContent'
                      }))
                      toast.success('已设置默认API地址')
                    }}
                    className="px-3 py-1 bg-primary/20 hover:bg-primary/30 text-primary text-sm rounded-md transition-colors"
                  >
                    设置默认地址
                  </button>
                </div>
                
                {/* 配额信息提示 */}
                <div className="mb-4 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-md">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <svg className="w-5 h-5 text-yellow-400 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h5 className="text-sm font-medium text-yellow-400">API配额提醒</h5>
                      <p className="text-sm text-yellow-300 mt-1">
                        Gemini API有免费配额限制。如果遇到429错误，请检查你的
                        <a href="https://aistudio.google.com/" target="_blank" rel="noopener noreferrer" className="underline hover:text-yellow-200">
                          Google AI Studio配额
                        </a>
                        或稍后重试。
                      </p>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-dark-300">语言大模型</span>
                      <div className={`px-2 py-1 rounded-full text-xs ${
                        settings.languageApiKey && settings.languageApiUrl 
                          ? 'bg-green-500/20 text-green-400' 
                          : 'bg-red-500/20 text-red-400'
                      }`}>
                        {settings.languageApiKey && settings.languageApiUrl ? '已配置' : '未配置'}
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-dark-300 text-sm">连接状态</span>
                      <div className={`px-2 py-1 rounded-full text-xs ${
                        connectionStatus.language.testing 
                          ? 'bg-yellow-500/20 text-yellow-400' 
                          : connectionStatus.language.connected 
                            ? 'bg-green-500/20 text-green-400' 
                            : 'bg-red-500/20 text-red-400'
                      }`}>
                        {connectionStatus.language.testing ? '测试中...' : 
                         connectionStatus.language.connected ? '已连接' : '未连接'}
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-dark-300">文生图模型</span>
                      <div className={`px-2 py-1 rounded-full text-xs ${
                        settings.imageApiKey && settings.imageApiUrl 
                          ? 'bg-green-500/20 text-green-400' 
                          : 'bg-red-500/20 text-red-400'
                      }`}>
                        {settings.imageApiKey && settings.imageApiUrl ? '已配置' : '未配置'}
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-dark-300 text-sm">连接状态</span>
                      <div className={`px-2 py-1 rounded-full text-xs ${
                        connectionStatus.image.testing 
                          ? 'bg-yellow-500/20 text-yellow-400' 
                          : connectionStatus.image.connected 
                            ? 'bg-green-500/20 text-green-400' 
                            : 'bg-red-500/20 text-red-400'
                      }`}>
                        {connectionStatus.image.testing ? '测试中...' : 
                         connectionStatus.image.connected ? '已连接' : '未连接'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* 语言大模型设置 */}
              <div className="bg-dark-700 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center">
                    <Brain size={20} className="mr-2 text-secondary" />
                    <h4 className="text-lg font-medium text-white">语言大模型配置</h4>
                  </div>
                  <a 
                    href="https://aistudio.google.com/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-xs text-primary hover:text-primary/80 underline"
                  >
                    获取API Key
                  </a>
                </div>
                
                <div className="space-y-4">
                  {/* 模型选择 */}
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      选择语言模型
                    </label>
                    <select
                      value={settings.languageModel}
                      onChange={(e) => handleSettingsChange('languageModel', e.target.value)}
                      className="w-full bg-dark-600 border border-dark-500 text-white rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/50"
                    >
                      <option value="gemini-2.5-pro">Gemini 2.5 Pro (默认)</option>
                      <option value="deepseek-chat">DeepSeek Chat</option>
                    </select>
                  </div>

                  {/* API Key */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-white">
                        API Key
                      </label>
                      {settings.languageApiKey && (
                        <div className="flex items-center space-x-2">
                          <span className="text-xs text-green-400 bg-green-500/20 px-2 py-1 rounded-full">
                            ✓ 已保存
                          </span>
                          <button
                            onClick={() => {
                              const confirmed = window.confirm('确定要清除保存的API Key吗？')
                              if (confirmed) {
                                setSettings(prev => ({ ...prev, languageApiKey: '' }))
                                settingsService.updateSetting('languageApiKey', '')
                                toast.success('API Key已清除')
                              }
                            }}
                            className="text-xs text-red-400 hover:text-red-300 underline"
                          >
                            清除
                          </button>
                        </div>
                      )}
                    </div>
                    <input
                      type="password"
                      value={settings.languageApiKey}
                      onChange={(e) => handleSettingsChange('languageApiKey', e.target.value)}
                      placeholder="请输入语言模型API密钥"
                      className="w-full bg-dark-600 border border-dark-500 text-white rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/50"
                    />
                    {settings.languageApiKey && (
                      <p className="text-xs text-dark-400 mt-1">
                        💾 API Key将自动保存到本地存储，下次打开时无需重新输入
                      </p>
                    )}
                  </div>

                  {/* API URL */}
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      API 接口地址
                    </label>
                    <input
                      type="url"
                      value={settings.languageApiUrl}
                      onChange={(e) => handleSettingsChange('languageApiUrl', e.target.value)}
                      placeholder="https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-pro:generateContent"
                      className="w-full bg-dark-600 border border-dark-500 text-white rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/50"
                    />
                  </div>

                  {/* API测试按钮 */}
                  <div className="flex justify-end">
                    <button
                      onClick={() => handleTestLanguageApiConnection()}
                      className="px-4 py-2 bg-secondary hover:bg-secondary/80 text-white rounded-md transition-colors text-sm"
                    >
                      测试语言模型连接
                    </button>
                  </div>
                </div>
              </div>

              {/* 文生图大模型设置 */}
              <div className="bg-dark-700 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center">
                    <Image size={20} className="mr-2 text-primary" />
                    <h4 className="text-lg font-medium text-white">文生图大模型配置</h4>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => {
                        window.open('quick-api-setup.html', '_blank')
                      }}
                      className="px-3 py-1 bg-primary/20 hover:bg-primary/30 text-primary text-xs rounded-md transition-colors"
                    >
                      🔧 快速配置
                    </button>
                    <a 
                      href={settings.imageModel === 'doubao-seedream-4-0' ? "https://www.doubao.com/" : 
                           "https://aistudio.google.com/"} 
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-primary hover:text-primary/80 underline"
                    >
                      {settings.imageModel === 'doubao-seedream-4-0' ? "获取豆包API Key" : 
                       "获取API Key"}
                    </a>
                  </div>
                </div>
                
                <div className="space-y-4">
                  {/* 模型选择 */}
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      选择模型
                    </label>
                    <select
                      value={settings.imageModel}
                      onChange={(e) => handleSettingsChange('imageModel', e.target.value)}
                      className="w-full bg-dark-600 border border-dark-500 text-white rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/50"
                    >
                      <option value="doubao-seedream-4-0">豆包-seedream-4.0 (最新推荐)</option>
                      <option value="gemini-2.5-flash-image-preview">Gemini 2.5 Flash Image Preview</option>
                      <option value="comfyui-local">ComfyUI 本地模型</option>
                    </select>
                  </div>

                  {/* API Key */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-white">
                        API Key
                      </label>
                      {settings.imageApiKey && (
                        <div className="flex items-center space-x-2">
                          <span className="text-xs text-green-400 bg-green-500/20 px-2 py-1 rounded-full">
                            ✓ 已保存
                          </span>
                          <button
                            onClick={() => {
                              const confirmed = window.confirm('确定要清除保存的图像API Key吗？')
                              if (confirmed) {
                                setSettings(prev => ({ ...prev, imageApiKey: '' }))
                                settingsService.updateSetting('imageApiKey', '')
                                toast.success('图像API Key已清除')
                              }
                            }}
                            className="text-xs text-red-400 hover:text-red-300 underline"
                          >
                            清除
                          </button>
                        </div>
                      )}
                    </div>
                    <input
                      type="password"
                      value={settings.imageApiKey}
                      onChange={(e) => handleSettingsChange('imageApiKey', e.target.value)}
                      placeholder={settings.imageModel === 'doubao-seedream-4-0' ? "请输入豆包API密钥" : 
                                   "请输入API密钥"}
                      className="w-full bg-dark-600 border border-dark-500 text-white rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/50"
                    />
                    {settings.imageApiKey && (
                      <p className="text-xs text-dark-400 mt-1">
                        💾 API Key将自动保存到本地存储，下次打开时无需重新输入
                      </p>
                    )}
                  </div>

                  {/* API URL */}
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      API 接口地址
                    </label>
                    <input
                      type="url"
                      value={settings.imageApiUrl}
                      onChange={(e) => handleSettingsChange('imageApiUrl', e.target.value)}
                      placeholder={settings.imageModel === 'doubao-seedream-4-0' ? "/api/doubao" :
                                   "https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash-image-preview:generateContent"}
                      className="w-full bg-dark-600 border border-dark-500 text-white rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/50"
                    />
                  </div>

                  {/* API测试按钮 */}
                  <div className="flex justify-end">
                    <button
                      onClick={handleTestApiConnection}
                      className="px-4 py-2 bg-secondary hover:bg-secondary/80 text-white rounded-md transition-colors text-sm"
                    >
                      测试API连接
                    </button>
                  </div>
                </div>
              </div>


              {/* 其他设置 */}
              <div className="bg-dark-700 rounded-lg p-4">
                <div className="flex items-center mb-4">
                  <Brain size={20} className="mr-2 text-secondary" />
                  <h4 className="text-lg font-medium text-white">其他配置</h4>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h5 className="text-white font-medium">自动保存</h5>
                      <p className="text-dark-300 text-sm">自动保存编辑内容</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        className="sr-only peer" 
                        checked={settings.autoSave}
                        onChange={(e) => handleSettingsChange('autoSave', e.target.checked)}
                      />
                      <div className="w-11 h-6 bg-dark-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <h5 className="text-white font-medium">深色模式</h5>
                      <p className="text-dark-300 text-sm">使用深色主题</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        className="sr-only peer" 
                        checked={settings.darkMode}
                        onChange={(e) => handleSettingsChange('darkMode', e.target.checked)}
                      />
                      <div className="w-11 h-6 bg-dark-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* 操作按钮 */}
            <div className="flex justify-between items-center gap-3 mt-6 pt-4 border-t border-dark-600">
              <button
                onClick={() => {
                  const confirmed = window.confirm('确定要清除所有保存的设置吗？这将删除所有API Key和配置信息。')
                  if (confirmed) {
                    settingsService.resetSettings()
                    setSettings(settingsService.getSettings())
                    setConnectionStatus({
                      language: { connected: false, testing: false },
                      image: { connected: false, testing: false }
                    })
                    toast.success('所有设置已清除')
                  }
                }}
                className="px-4 py-2 text-red-400 hover:text-red-300 transition-colors text-sm border border-red-500/20 rounded-md hover:bg-red-500/10"
              >
                清除所有设置
              </button>
              
              <div className="flex gap-3">
                <button
                  onClick={() => setShowSettings(false)}
                  className="px-4 py-2 text-dark-300 hover:text-white transition-colors"
                >
                  取消
                </button>
                <button
                  onClick={handleSaveSettings}
                  className="flex items-center px-4 py-2 bg-primary hover:bg-primary/80 text-white rounded-md transition-colors"
                >
                  <Save size={16} className="mr-2" />
                  保存设置
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 认证模态框 */}
      {showAuthModal && (
        <AuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          onSuccess={handleAuthSuccess}
        />
      )}

      {/* 用户资料模态框 */}
      {showUserProfile && (
        <UserProfile
          isOpen={showUserProfile}
          onClose={() => setShowUserProfile(false)}
          onLogout={handleLogout}
        />
      )}

      {/* 用户设置模态框 */}
      {showUserSettings && (
        <UserSettings
          isOpen={showUserSettings}
          onClose={() => setShowUserSettings(false)}
        />
      )}

      {/* 帮助模态框 */}
      {showHelp && (
        <HelpModal
          isOpen={showHelp}
          onClose={() => setShowHelp(false)}
        />
      )}

      {/* 用户中心 */}
      {showUserCenter && (
        <UserCenter
          isOpen={showUserCenter}
          onClose={() => setShowUserCenter(false)}
        />
      )}
    </div>
  )
}
